import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "./serengeti.css";
import "./lake-manyara.css";

const features = [
  {
    title: "Groundwater forest",
    text: "Tall trees and cool shade sit against the Rift escarpment. The forest feels intimate, green, and very different from open Serengeti plains.",
  },
  {
    title: "Lake edge wildlife",
    text: "Hippos, waterbirds, and grazing herds use the shoreline. When water levels are right, flamingos can turn the shallows pink.",
  },
  {
    title: "Tree climbing lions",
    text: "Manyara is known for lions that rest in trees. Sightings are never guaranteed, but the habit is part of the park’s story.",
  },
  {
    title: "Compact northern stop",
    text: "A smaller park that fits cleanly between Tarangire and Ngorongoro. Ideal for one night or a focused full day drive.",
  },
];

const parkStats = [
  { value: "325", label: "km² national park" },
  { value: "400+", label: "bird species" },
  { value: "Rift", label: "Valley escarpment" },
  { value: "1 night", label: "typical stay" },
];

const experiences = [
  {
    id: "forest-drives",
    name: "Groundwater Forest Drives",
    days: "Half day to 1 night",
    style: "Forest game drives",
    lodging: "Lodge or tented camp nearby",
    bestFor: "Shade, elephants, and a softer woodland mood",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Lush highland and forest landscape",
    summary:
      "Drive under canopy and along forest tracks where elephants move quietly and light filters through tall trunks near the escarpment wall.",
    body: "The groundwater forest is Manyara’s signature habitat. It cools the day, frames the cliffs, and gives a close, enclosed feel after wider parks. Guides watch for primates, elephants, and the odd lion resting above the track. It is a good contrast day if your circuit already includes open plains.",
    highlights: [
      "Tall forest against the Rift escarpment",
      "Elephants and monkeys in shaded habitat",
      "Cooler microclimate than the dry plains",
      "Strong atmosphere for photography",
    ],
    itinerary: [
      { day: "Morning", text: "Enter the park and work forest loops near the escarpment." },
      { day: "Late morning", text: "Continue toward open glades and lake approaches." },
      { day: "Picnic", text: "Lunch at an approved site if doing a full day." },
      { day: "Exit", text: "Return toward Karatu, Tarangire, or your lodge." },
    ],
    notes:
      "Forest tracks can feel busy at peak hours. An early start keeps the day calmer.",
  },
  {
    id: "lake-shore",
    name: "Lake Shore and Floodplains",
    days: "Morning or full day",
    style: "Birding and shoreline game",
    lodging: "Nearby lodge or camp",
    bestFor: "Flamingos, hippos, and open lake views",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open water and shoreline under wide sky",
    summary:
      "Follow the alkaline lake edge for hippo pools, wading birds, and wide views across the water toward the distant shore.",
    body: "The lake is the park’s open stage. Water levels rise and fall with rain, so flamingo numbers and shoreline access change through the year. Even without flamingos, the floodplains hold grazing game and excellent birding, with the escarpment always in the background.",
    highlights: [
      "Hippo pools and waterbird congregations",
      "Flamingo seasons when water chemistry suits them",
      "Open views across Lake Manyara",
      "A soft counterpoint to forest drives",
    ],
    itinerary: [
      { day: "Morning", text: "Drive from forest edge onto floodplain tracks." },
      { day: "Shore", text: "Slow viewing along hippo pools and birding spots." },
      { day: "Midday", text: "Rest or picnic depending on the day plan." },
      { day: "Afternoon", text: "Optional second loop before exiting the park." },
    ],
    notes:
      "We treat flamingo sightings as a bonus. The shoreline is worth the drive either way.",
  },
  {
    id: "escarpment",
    name: "Rift Escarpment Views",
    days: "Viewpoint stops",
    style: "Scenic overlooks",
    lodging: "Escarpment or lakeside lodge",
    bestFor: "Landscape, sunset, and orientation",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Dramatic escarpment and open valley views",
    summary:
      "The western Rift wall rises above the park. Lodges and viewpoints on or near the escarpment give sweeping looks over forest and lake.",
    body: "Many guests remember Manyara as much for the setting as for the checklist. Standing above the park, you see how forest, floodplain, and water fit together in a narrow strip along the valley floor. Sunset from the escarpment is a quiet highlight on a one night stay.",
    highlights: [
      "Classic Rift Valley scenery",
      "Useful orientation before or after a park drive",
      "Strong lodge viewpoints in the area",
      "Easy to include without adding a full extra day",
    ],
    itinerary: [
      { day: "Arrival", text: "Check in and visit a nearby overlook if time allows." },
      { day: "Safari day", text: "Full or half day park drive below." },
      { day: "Evening", text: "Escarpment sundowner or lodge terrace views." },
      { day: "Next", text: "Continue to Ngorongoro or return toward Tarangire and Arusha." },
    ],
    notes:
      "Ask for escarpment lodging if views matter as much as wildlife time.",
  },
  {
    id: "circuit-link",
    name: "Circuit Link Day",
    days: "1 night or day visit",
    style: "Northern loop connector",
    lodging: "Karatu or Manyara area lodge",
    bestFor: "Bridging Tarangire and Ngorongoro smoothly",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open road through highland landscape",
    summary:
      "Manyara sits on the natural road between Tarangire and the crater highlands, so it works as a short, scenic wildlife stop without stretching the itinerary.",
    body: "On private circuits we often use Manyara as a one night bridge. You keep travel days productive, add forest and lake habitats your Serengeti days will not repeat, and arrive at Ngorongoro without a pure transfer day. Guests short on time can also do a morning drive and continue the same afternoon.",
    highlights: [
      "Logical place in a classic northern sequence",
      "Adds habitat variety without long detours",
      "Flexible as overnight or day visit",
      "Pairs well with a Zanzibar finish later",
    ],
    itinerary: [
      { day: "From Tarangire", text: "Morning departure and midday Manyara drive." },
      { day: "Overnight", text: "Lodge near the park or in Karatu." },
      { day: "Next morning", text: "Optional short drive, then up to Ngorongoro." },
      { day: "Day visit option", text: "Enter early, exit by lunch, continue to the crater." },
    ],
    notes:
      "Tell us your night count. We will keep Manyara sharp so it never feels like filler.",
  },
];

const seasons = [
  {
    title: "Dry months",
    text: "June to October. Clearer forest tracks, reliable game near water, and strong escarpment views.",
  },
  {
    title: "Green months",
    text: "November to May. Lush forest, excellent birding, and softer light along the lake edge.",
  },
  {
    title: "Flamingo windows",
    text: "Numbers rise and fall with lake chemistry and water levels. We treat them as seasonal luck, not a promise.",
  },
  {
    title: "Best use",
    text: "Year round as a compact stop. Dry months feel easiest for first time northern circuits.",
  },
];

const practical = [
  {
    title: "How long to stay",
    text: "A morning or one night is enough for most guests. Two nights suit birders or a slower highland pace.",
  },
  {
    title: "Park size",
    text: "Manyara is compact. You will not cover Serengeti distances, so the day stays focused and manageable.",
  },
  {
    title: "What we arrange",
    text: "Park fees, private guide and 4x4, nearby lodging, and the clean link to Tarangire or Ngorongoro.",
  },
];

function LakeManyara() {
  const { hash } = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Lake Manyara · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStatsVisible(true);
        observer.disconnect();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page lake-manyara-page">
      <section className="kili-hero serengeti-hero lake-manyara-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7">
              <p className="eyebrow eyebrow--light mb-3">
                <Link
                  to="/safaris/northern-circuits"
                  className="text-decoration-none"
                >
                  Northern Circuit
                </Link>
              </p>
              <h1 className="mb-3">Lake Manyara</h1>
              <p className="kili-hero__lede mb-4">
                Groundwater forest, Rift escarpment, and a shallow alkaline
                lake. A compact northern park that adds shade, birds, and
                shoreline life between Tarangire and Ngorongoro.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#experiences">
                  View experiences
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Manyara
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible ? "serengeti-stats is-visible" : "serengeti-stats"
        }
        aria-label="Lake Manyara park facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {parkStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">The park</p>
              <h2 className="mb-3">Why Manyara earns its place on the loop</h2>
              <p className="page-lead mb-0">
                It is small, scenic, and full of contrast. Forest, lake, and
                escarpment sit close together, so a short stay still feels
                complete.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item) => (
              <div className="col-md-6" key={item.title}>
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experiences" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Experiences</p>
              <h2 className="mb-3">How to use a Manyara day</h2>
              <p className="page-lead mb-0">
                Forest loops, lake shore birding, escarpment views, and a clean
                link into the wider northern circuit. Choose the mix that fits
                your nights.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {experiences.map((item, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={item.id}
                id={item.id}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={item.image} alt={item.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {item.days}
                      <span aria-hidden="true"> · </span>
                      {item.style}
                    </p>
                    <h3 className="h3 mb-3">{item.name}</h3>
                    <p className="kili-route__summary">{item.summary}</p>
                    <p className="kili-route__body-copy mb-0">{item.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{item.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{item.days}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{item.lodging}</dd>
                    </div>
                    <div>
                      <dt>Style</dt>
                      <dd>{item.style}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the day</h4>
                      <ul className="trek-points mb-0">
                        {item.highlights.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {item.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{item.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {item.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Seasons</p>
              <h2 className="mb-3">When to visit Lake Manyara</h2>
              <p className="page-lead mb-0">
                The park works year round as a short stop. Season mainly changes
                greenery, birds, and how the lake edge looks.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {seasons.map((item) => (
              <div className="col-md-6 col-lg-3" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">Tell us if Manyara is a day visit or an overnight bridge.</h2>
              <p className="mb-0 home-close__copy">
                We reply with timing, lodge options, and how it sits between
                Tarangire, Ngorongoro, and the rest of your northern loop.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LakeManyara;
